// Drive — folder/sub-folder/file grid with version management. Same layout as References.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { Loading } from "../../shared/components/Empty";
import { toast } from "../../shared/stores/toasts";
import { I } from "../../shared/Icon";
import { useAuth } from "../../shared/stores/auth";

// ── Types ────────────────────────────────────────────────────────────────────

type DriveFile = {
  id: number; name: string; display_name: string | null; size_bytes: number; mime_type: string | null;
  scope: string; folder_id: number | null; current_version_id: number | null;
  version_count: number; description: string | null; created_at: string;
};
type DriveFolder = { id: number; name: string; parent_id: number | null; scope: string; sort_order: number };
type DriveVersion = {
  id: number; version_no: number; size_bytes: number;
  uploaded_by: number | null; uploaded_by_name: string | null; uploaded_at: string;
};

const ALL = "all";
// 업로드 한도 — 로그인 역할별 (서버 /drive/upload-limit가 결정). null = 무제한.
// 서버 응답 전까지의 기본값이며, 최종 검증은 백엔드가 수행한다.
let uploadLimitBytes: number | null = 5 * 1024 ** 3;
let uploadLimitLabel = "5 GB";
const SCOPE_LABELS: Record<string, string> = { PERSONAL: "개인", DEPARTMENT: "부서", COMPANY: "전사", SECRET: "내부문서" };

// ── Helpers ───────────────────────────────────────────────────────────────────

function isImage(f: DriveFile)  {
  if (f.mime_type?.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|svg|bmp|ico|tiff?)$/i.test(f.name);
}
function isPdf(f: DriveFile)    { return f.mime_type === "application/pdf" || /\.pdf$/i.test(f.name); }
function isVideo(f: DriveFile)  {
  if (f.mime_type?.startsWith("video/")) return true;
  return /\.(mp4|webm|mov|mkv|avi)$/i.test(f.name);
}
function isText(f: DriveFile)   {
  if (f.mime_type?.startsWith("text/")) return true;
  return /\.(txt|md|csv|html?|xml|json|ya?ml|sh|sql|py|js|ts|jsx|tsx|css|scss|c|cpp|h|java|go|rs)$/i.test(f.name);
}
function isPyFile(f: DriveFile) { return /\.py$/i.test(f.name); }
function isOffice(f: DriveFile) {
  const m = f.mime_type ?? "";
  return m.includes("word") || m.includes("excel") || m.includes("powerpoint") ||
    m.includes("spreadsheet") || m.includes("presentation") ||
    /\.(docx?|xlsx?|pptx?|odt|ods|odp)$/i.test(f.name);
}
function isHwp(f: DriveFile)    { return /\.hwpx?$/i.test(f.name); }
function fileEmoji(f: DriveFile) {
  if (isImage(f))  return "🖼️";
  if (isPdf(f))    return "📄";
  if (isVideo(f))  return "🎬";
  if (isText(f))   return "📝";
  if (isOffice(f)) return "📊";
  const n = f.name.toLowerCase();
  if (n.endsWith(".zip") || n.endsWith(".rar") || n.endsWith(".7z")) return "🗜️";
  if (n.endsWith(".hwp") || n.endsWith(".hwpx")) return "📋";
  return "📁";
}
function stripExt(name: string) { const i = name.lastIndexOf("."); return i > 0 ? name.slice(0, i) : name; }
const dispName = (f: DriveFile) => f.display_name || f.name;

function FileThumbnail({ f, style }: { f: DriveFile; style?: React.CSSProperties }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!isImage(f) && !isPdf(f)) return;
    let url: string;
    api.get(`/drive/files/${f.id}/thumbnail`, { responseType: "blob" })
      .then(r => { url = URL.createObjectURL(r.data as Blob); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [f.id]);
  if (src) return <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", ...style }} />;
  return <span style={{ fontSize: 40, lineHeight: 1 }}>{fileEmoji(f)}</span>;
}
function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
}
function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("ko-KR", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
const fileKey = (f: DriveFile) => String(f.id);

async function downloadFile(f: DriveFile) {
  const res = await api.get(`/drive/files/${f.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a"); a.href = url; a.download = f.name; a.click();
  URL.revokeObjectURL(url);
}

// 스트리밍 다운로드 — 디스크에서 직접 받아 브라우저 메모리 버퍼링 없이 저장 (대용량 ISO 등).
function streamDownloadFile(f: DriveFile) {
  const token = useAuth.getState().accessToken ?? "";
  const base = (api.defaults.baseURL ?? "").replace(/\/$/, "");
  const url = `${base}/drive/files/${f.id}/download?stream=1&token=${encodeURIComponent(token)}`;
  const a = document.createElement("a"); a.href = url; a.download = f.name; a.rel = "noopener"; a.click();
}

function TextViewer({ src }: { src: string }) {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => {
    fetch(src).then(r => r.text()).then(setText).catch(() => setText("(텍스트 로드 실패)"));
  }, [src]);
  return (
    <pre style={{ margin: 0, padding: "14px 18px", fontSize: 12, lineHeight: 1.65,
      color: "#d4d4d4", fontFamily: "'JetBrains Mono', Consolas, 'Courier New', monospace",
      overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
      background: "#1e1e2e", width: "100%", height: "100%", boxSizing: "border-box" }}>
      {text ?? "⏳ 불러오는 중…"}
    </pre>
  );
}

// ── File detail modal (preview + version history) ─────────────────────────────

function FileDetail({ files, initialIndex, onClose, onDeleted, onVersionUploaded }: {
  files: DriveFile[]; initialIndex: number;
  onClose: () => void; onDeleted: () => void; onVersionUploaded: () => void;
}) {
  const [idx, setIdx] = useState(initialIndex);
  const [f, setF] = useState<DriveFile>(files[initialIndex] ?? files[0]);
  useEffect(() => {
    const next = files[idx] ?? files[0];
    setF(next);
    setDraftDesc(next?.description ?? "");
    setDraftDisplayName(next?.display_name ?? "");
    setPreviewVersion(null);
  }, [idx, files]);

  const [src, setSrc] = useState<string | null>(null);
  const [srcMime, setSrcMime] = useState<string | null>(null);
  const [versions, setVersions] = useState<DriveVersion[] | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [previewDragOver, setPreviewDragOver] = useState(false);
  const [draftDesc, setDraftDesc] = useState<string>(files[initialIndex]?.description ?? "");
  const [draftDisplayName, setDraftDisplayName] = useState<string>(files[initialIndex]?.display_name ?? "");
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);
  const [versionPct, setVersionPct] = useState<number | null>(null);
  const versionInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const previewDragCounter = useRef(0);

  // ── Gemini AI edit state ──────────────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(280);
  const containerRef = useRef<HTMLDivElement>(null);
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = panelWidth;
    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.clientX;
      setPanelWidth(Math.max(150, Math.min(520, startW + delta)));
    };
    const onUp = () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const [showGemini,    setShowGemini]    = useState(false);
  const geminiKey = "AIzaSyC5Gqn34Dxab7ngpw-qeimLVYNnwm5h2uE";
  const [geminiInst,    setGeminiInst]    = useState("");
  const [geminiResult,  setGeminiResult]  = useState<string | null>(null);
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [originalText,  setOriginalText]  = useState<string | null>(null);
  const [showModified,  setShowModified]  = useState(false);

  // Reset when file changes
  useEffect(() => {
    setShowGemini(false); setGeminiResult(null); setOriginalText(null); setShowModified(false);
  }, [f?.id]);

  // Fetch code text when entering Gemini mode
  useEffect(() => {
    if (!showGemini || !f) return;
    setOriginalText(null);
    api.get(`/drive/files/${f.id}/download`, { responseType: "blob" })
      .then(async (r) => { setOriginalText(await (r.data as Blob).text()); })
      .catch(() => toast.error("코드 로드 실패"));
  }, [showGemini, f?.id]);

  const runGemini = async () => {
    if (!geminiInst.trim()) { toast.error("수정 지시사항을 입력하세요"); return; }
    if (!originalText) { toast.error("코드를 불러오는 중입니다"); return; }
    setGeminiLoading(true);
    const prompt = `You are an expert Python code editor. Modify the following Python code according to the instruction below.
Return ONLY the modified code — no explanations, no markdown fences, no extra text.

Instruction: ${geminiInst}

Current code:
${originalText}`;
    try {
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${geminiKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { temperature: 0.1, maxOutputTokens: 8192 },
          }),
        }
      );
      if (!res.ok) {
        const e = await res.json();
        throw new Error(e?.error?.message ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      let result: string = data?.candidates?.[0]?.content?.parts?.[0]?.text ?? "";
      result = result.replace(/^```[\w]*\n?/, "").replace(/\n?```$/, "").trim();
      setGeminiResult(result);
      setShowModified(true);
      toast.success("AI 수정 완료");
    } catch (e: any) {
      toast.error("Gemini 오류", e?.message ?? "");
    } finally {
      setGeminiLoading(false);
    }
  };

  const saveGeminiResult = async () => {
    if (!geminiResult || !f) return;
    const blob = new Blob([geminiResult], { type: "text/plain" });
    const file = new File([blob], f.name, { type: "text/plain" });
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post<DriveFile>(`/drive/files/${f.id}/versions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setF(r.data); loadVersions(); onVersionUploaded();
      toast.success("새 버전으로 저장됨", `v${r.data.version_count}`);
      setShowGemini(false); setGeminiResult(null); setShowModified(false);
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.detail ?? ""); }
  };

  // Load preview (current version or a specific version)
  useEffect(() => {
    setSrc(null);
    setSrcMime(null);
    if (!f) return;
    let url: string;
    const canPreview = isImage(f) || isPdf(f) || isVideo(f) || isText(f) || isHwp(f) || isOffice(f);
    if (!canPreview) { setSrc("unsupported"); return; }
    const endpoint = previewVersion != null
      ? `/drive/files/${f.id}/preview?version_no=${previewVersion}`
      : `/drive/files/${f.id}/preview`;
    api.get(endpoint, { responseType: "blob" })
      .then((r) => {
        const respMime = (r.headers["content-type"] as string | undefined)?.split(";")[0].trim() ?? null;
        setSrcMime(respMime);
        url = URL.createObjectURL(r.data as Blob);
        setSrc(url);
      })
      .catch((e: any) => {
        if (e?.response?.status === 415) setSrc("unsupported");
        else setSrc("err");
      });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [f?.id, previewVersion]);

  // Load versions
  const loadVersions = useCallback(() => {
    if (!f) return;
    api.get<DriveVersion[]>(`/drive/files/${f.id}/versions`)
      .then((r) => setVersions(r.data))
      .catch(() => setVersions([]));
  }, [f?.id]);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const deleteOpen = async () => {
    if (!f) return;
    if (!confirm(`"${f.name}"을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/drive/files/${f.id}`); toast.success("삭제됨"); onDeleted(); onClose(); }
    catch { toast.error("삭제 실패"); }
  };

  // Arrow keys + F2 + Del (delete this file)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "F2") { e.preventDefault(); beginRename(); return; }
      if (editingName) return;
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "Delete") { e.preventDefault(); deleteOpen(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); setIdx(i => Math.max(0, i - 1)); }
      if (e.key === "ArrowRight") { e.preventDefault(); setIdx(i => Math.min(files.length - 1, i + 1)); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [editingName, files.length, f?.name]);

  const beginRename = () => {
    if (!f) return;
    const dot = f.name.lastIndexOf(".");
    setDraftName(dot > 0 ? f.name.slice(0, dot) : f.name);
    setEditingName(true);
  };
  const commitRename = async () => {
    setEditingName(false);
    if (!f) return;
    const dot = f.name.lastIndexOf(".");
    const ext = dot > 0 ? f.name.slice(dot) : "";
    const fullName = draftName.trim() + ext;
    if (!draftName.trim() || fullName === f.name) return;
    try {
      const r = await api.patch<DriveFile>(`/drive/files/${f.id}`, { name: fullName });
      setF(r.data); toast.success("이름 변경됨");
    } catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.detail ?? ""); }
  };

  const saveDesc = async (value: string) => {
    if (!f || value === (f.description ?? "")) return;
    try {
      const r = await api.patch<DriveFile>(`/drive/files/${f.id}`, { description: value || null });
      setF(r.data);
    } catch { toast.error("설명 저장 실패"); }
  };

  const saveDisplayName = async (value: string) => {
    if (!f || value === (f.display_name ?? "")) return;
    try {
      const r = await api.patch<DriveFile>(`/drive/files/${f.id}`, { display_name: value || null });
      setF(r.data);
    } catch { toast.error("별명 저장 실패"); }
  };

  const uploadVersion = async (picked: File) => {
    if (!f) return;
    if (uploadLimitBytes !== null && picked.size > uploadLimitBytes) { toast.error("파일 크기 초과", `최대 ${uploadLimitLabel}까지 업로드 가능합니다`); return; }
    const fd = new FormData(); fd.append("file", picked);
    setVersionPct(0);
    try {
      const r = await api.post<DriveFile>(`/drive/files/${f.id}/versions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setVersionPct(Math.round(e.loaded / e.total * 100)); },
      });
      setF(r.data); loadVersions(); onVersionUploaded();
      toast.success("새 버전 업로드됨", `v${r.data.version_count}`);
    } catch (e: any) { toast.error("업로드 실패", e?.response?.data?.detail ?? ""); }
    setVersionPct(null);
    if (versionInputRef.current) versionInputRef.current.value = "";
  };

  const deleteVersion = async (versionNo: number) => {
    if (!f) return;
    if (!confirm(`v${versionNo}을 영구 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/drive/files/${f.id}/versions/${versionNo}`);
      loadVersions();
      toast.success(`v${versionNo} 삭제됨`);
    } catch (e: any) { toast.error("삭제 실패", e?.response?.data?.detail ?? ""); }
  };

  const restoreVersion = async (versionNo: number) => {
    if (!f) return;
    if (!confirm(`v${versionNo}으로 복원하시겠습니까?`)) return;
    try {
      const r = await api.post<DriveFile>(`/drive/files/${f.id}/versions/${versionNo}/restore`);
      setF(r.data); loadVersions(); onVersionUploaded();
      toast.success(`v${versionNo}으로 복원됨`);
    } catch (e: any) { toast.error("복원 실패", e?.response?.data?.detail ?? ""); }
  };

  const downloadVersion = async (versionNo: number) => {
    const res = await api.get(`/drive/files/${f.id}/versions/${versionNo}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement("a"); a.href = url; a.download = `${stripExt(f.name)}_v${versionNo}`; a.click();
    URL.revokeObjectURL(url);
  };

  const isCurrent = (v: DriveVersion) => v.id === f?.current_version_id;

  if (!f) return null;
  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: "min(920px, 96vw)", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>{fileEmoji(f)}</span>
          {editingName ? (
            <>
              <input autoFocus className="t-input" style={{ flex: 1, fontSize: 14, fontWeight: 600, padding: "3px 8px" }}
                value={draftName} onChange={(e) => setDraftName(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditingName(false); }} />
              {f.name.lastIndexOf(".") > 0 && (
                <span style={{ color: "var(--text-3)", fontSize: 13, flexShrink: 0, userSelect: "none" }}>{f.name.slice(f.name.lastIndexOf("."))}</span>
              )}
            </>
          ) : (
            <span onClick={beginRename} title="클릭하여 이름 변경 (F2)"
              style={{ flex: 1, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text", padding: "2px 4px", borderRadius: 4 }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
              {f.name}
            </span>
          )}
          <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{fmtSize(f.size_bytes)}</span>
          <button className="t-btn" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} title="이전 파일 (←)" style={{ fontSize: 12 }}>◀</button>
          <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{idx + 1} / {files.length}</span>
          <button className="t-btn" onClick={() => setIdx(i => Math.min(files.length - 1, i + 1))} disabled={idx === files.length - 1} title="다음 파일 (→)" style={{ fontSize: 12 }}>▶</button>
          {isPyFile(f) && (
            <button className="t-btn" onClick={() => { setShowGemini(v => !v); setGeminiResult(null); setShowModified(false); }}
              title="Gemini AI로 코드 수정"
              style={{ fontSize: 12, background: showGemini ? "var(--p-500,#6366f1)" : undefined, color: showGemini ? "#fff" : undefined, borderColor: showGemini ? "var(--p-500,#6366f1)" : undefined }}>
              ✦ AI 수정
            </button>
          )}
          <button className="t-btn" onClick={() => downloadFile(f)} title="다운로드" style={{ fontSize: 12 }}>⬇️</button>
          <button className="t-btn" onClick={onClose}>✕</button>
        </div>

        <div ref={containerRef} style={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* Preview */}
          <div ref={previewRef}
            onDragEnter={(e) => { e.preventDefault(); previewDragCounter.current++; if (e.dataTransfer.types.includes("Files")) setPreviewDragOver(true); }}
            onDragLeave={() => { previewDragCounter.current--; if (previewDragCounter.current === 0) setPreviewDragOver(false); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); previewDragCounter.current = 0; setPreviewDragOver(false); const file = e.dataTransfer.files[0]; if (file) uploadVersion(file); }}
            style={{ position: "relative", flex: 1, minWidth: 0, background: "#0b0b0d", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: 8 }}>
            {previewDragOver && (
              <div style={{ position: "absolute", inset: 0, zIndex: 10, background: "rgba(99,102,241,.25)", border: "3px dashed #818cf8", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <div style={{ textAlign: "center", color: "#fff" }}>
                  <div style={{ fontSize: 36 }}>📤</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 8 }}>여기에 드롭하면 새 버전으로 업로드</div>
                </div>
              </div>
            )}
            {src && src !== "err" && src !== "unsupported" && (
              <button onClick={() => previewRef.current?.requestFullscreen?.()} title="전체화면"
                style={{ position: "absolute", top: 8, right: 8, zIndex: 3, background: "rgba(0,0,0,.55)", color: "#fff", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 13 }}>⛶</button>
            )}
            {previewVersion != null && (
              <div style={{ position: "absolute", top: 8, left: 8, zIndex: 3, background: "var(--p-500,#6366f1)", color: "#fff", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                v{previewVersion} 미리보기
                <button onClick={() => setPreviewVersion(null)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", marginLeft: 6, fontSize: 13, padding: 0 }}>✕</button>
              </div>
            )}
            {src === null && <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>불러오는 중…</span>}
            {src === "err" && <span style={{ color: "#ef4444" }}>미리보기 실패</span>}
            {src === "unsupported" && (
              <div style={{ textAlign: "center", color: "#fff" }}>
                <div style={{ fontSize: 64 }}>{fileEmoji(f)}</div>
                <div style={{ fontSize: 14, marginTop: 10, color: "rgba(255,255,255,.7)" }}>{f.name}</div>
                <div style={{ fontSize: 12, marginTop: 6, color: "rgba(255,255,255,.4)" }}>미리보기를 지원하지 않는 형식입니다</div>
                <button className="t-btn" onClick={() => downloadFile(f)} style={{ marginTop: 16, background: "rgba(255,255,255,.1)", color: "#fff", border: "1px solid rgba(255,255,255,.2)" }}>⬇️ 다운로드</button>
              </div>
            )}
            {/* Image */}
            {src && src !== "err" && src !== "unsupported" && (srcMime?.startsWith("image/") || (!srcMime && isImage(f))) && (
              <img src={src} alt={f.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            )}
            {/* Video */}
            {src && src !== "err" && src !== "unsupported" && (srcMime?.startsWith("video/") || (!srcMime && isVideo(f))) && (
              <video src={src} controls style={{ maxWidth: "100%", maxHeight: "100%" }} />
            )}
            {/* PDF or LibreOffice-converted document → embed */}
            {src && src !== "err" && src !== "unsupported" && (srcMime === "application/pdf" || (!srcMime && isPdf(f))) && (
              <embed src={src} type="application/pdf" style={{ width: "100%", height: "100%", border: 0 }} />
            )}
            {/* HWPX / HTML response */}
            {src && src !== "err" && src !== "unsupported" && srcMime?.startsWith("text/html") && (
              // allow-same-origin 을 함께 주면 안 된다: src 가 TimBox 출처의 blob 주소라,
              // 올린 HTML 의 스크립트가 TimBox 로 돌아 로그인 토큰을 읽을 수 있었다.
              <iframe src={src} title={f.name} sandbox="allow-scripts allow-popups"
                style={{ width: "100%", height: "100%", border: 0, background: "#fff", borderRadius: 4 }} />
            )}
            {src && src !== "err" && src !== "unsupported" && ((srcMime?.startsWith("text/") && !srcMime.startsWith("text/html")) || (!srcMime && isText(f))) && (
              showGemini ? (
                <div style={{ width: "100%", height: "68vh", display: "flex", flexDirection: "column" }}>
                  {/* Tab bar */}
                  <div style={{ display: "flex", background: "#161b2e", borderBottom: "1px solid #2a2e4a", flexShrink: 0 }}>
                    <button onClick={() => setShowModified(false)}
                      style={{ padding: "7px 18px", border: "none", cursor: "pointer", fontSize: 12, background: "transparent",
                        color: !showModified ? "#a5b4fc" : "#555",
                        borderBottom: !showModified ? "2px solid #6366f1" : "2px solid transparent" }}>
                      📄 원본
                    </button>
                    {geminiResult && (
                      <button onClick={() => setShowModified(true)}
                        style={{ padding: "7px 18px", border: "none", cursor: "pointer", fontSize: 12, background: "transparent",
                          color: showModified ? "#4ade80" : "#555",
                          borderBottom: showModified ? "2px solid #22c55e" : "2px solid transparent" }}>
                        ✦ 수정본
                      </button>
                    )}
                  </div>
                  <pre style={{ flex: 1, margin: 0, padding: "14px 18px", fontSize: 12, lineHeight: 1.65,
                    color: "#d4d4d4", fontFamily: "'JetBrains Mono', Consolas, 'Courier New', monospace",
                    overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
                    background: "#1e1e2e" }}>
                    {showModified && geminiResult ? geminiResult : (originalText ?? "⏳ 코드 불러오는 중…")}
                  </pre>
                </div>
              ) : (
                <TextViewer src={src} />
              )
            )}
          </div>

          {/* Resize handle */}
          <div onMouseDown={startResize}
            style={{ width: 5, cursor: "col-resize", flexShrink: 0, background: "var(--border)", transition: "background 0.15s" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--p-400,#818cf8)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--border)")} />

          {/* Right panel — Gemini AI panel or Version history */}
          {showGemini ? (
            /* ── Gemini AI Edit Panel ── */
            <div style={{ width: panelWidth, flexShrink: 0, borderLeft: "none", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13 }}>✦ Gemini AI 수정</span>
                <span style={{ fontSize: 10, background: "rgba(66,133,244,0.15)", color: "#4285F4", padding: "2px 7px", borderRadius: 8, fontWeight: 600 }}>2.5 Flash</span>
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
                {/* Instruction */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", display: "block", marginBottom: 4 }}>수정 지시사항 *</label>
                  <textarea className="t-input" value={geminiInst} onChange={e => setGeminiInst(e.target.value)}
                    placeholder={"예시:\n• 모든 주석을 영어로 번역\n• API_KEY를 환경변수로 분리\n• 각 함수에 docstring 추가\n• 에러 핸들링 강화\n• 타입 힌트 추가"}
                    style={{ resize: "vertical", minHeight: 130, fontSize: 12, lineHeight: 1.5, padding: "7px 9px" }} />
                </div>
                {/* Code stats */}
                {originalText ? (
                  <div style={{ fontSize: 11, color: "var(--text-3)", background: "var(--panel)", padding: "5px 9px", borderRadius: 6 }}>
                    📄 {f.name} · {originalText.split("\n").length}줄 · {fmtSize(new Blob([originalText]).size)}
                  </div>
                ) : showGemini && (
                  <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>⏳ 코드 불러오는 중…</div>
                )}
                {/* Result status */}
                {geminiResult && !geminiLoading && (
                  <div style={{ padding: "8px 10px", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 8, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                    ✅ 수정 완료 — 왼쪽에서 원본/수정본 확인
                  </div>
                )}
              </div>
              {/* Action buttons */}
              <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 6 }}>
                <button className="t-btn t-btn-primary" onClick={runGemini}
                  disabled={geminiLoading || !originalText || !geminiInst.trim()}
                  style={{ width: "100%", fontSize: 12 }}>
                  {geminiLoading ? "✦ 수정 중…" : "✦ 수정 시작"}
                </button>
                {geminiResult && (
                  <button onClick={saveGeminiResult}
                    style={{ width: "100%", fontSize: 12, padding: "7px 0", border: "1px solid rgba(34,197,94,0.4)", borderRadius: 8, cursor: "pointer", background: "rgba(34,197,94,0.12)", color: "#16a34a", fontWeight: 600 }}>
                    💾 새 버전으로 저장
                  </button>
                )}
                {geminiResult && (
                  <button className="t-btn" onClick={() => { setGeminiResult(null); setGeminiInst(""); setShowModified(false); }}
                    style={{ width: "100%", fontSize: 11 }}>
                    ↩ 다시 수정
                  </button>
                )}
              </div>
            </div>
          ) : (
          /* ── Version history panel ── */
          <div style={{ width: panelWidth, flexShrink: 0, borderLeft: "none", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--border)", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
              <span>🕓 버전 이력</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)", fontWeight: 400 }}>{versions ? `${versions.length}개` : ""}</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
              {versions === null && <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--text-3)" }}>불러오는 중…</div>}
              {versions?.length === 0 && <div style={{ padding: "20px 14px", fontSize: 12, color: "var(--text-3)", textAlign: "center" }}>버전 없음</div>}
              {versions?.map((v) => (
                <div key={v.id} style={{ padding: "8px 14px", borderBottom: "1px solid var(--border)", display: "flex", flexDirection: "column", gap: 4, background: isCurrent(v) ? "rgba(99,102,241,.06)" : undefined }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: isCurrent(v) ? "var(--p-500,#6366f1)" : "var(--panel)", color: isCurrent(v) ? "#fff" : "var(--text-2)" }}>v{v.version_no}</span>
                    {isCurrent(v) && <span style={{ fontSize: 10, color: "var(--p-500,#6366f1)", fontWeight: 600 }}>현재</span>}
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-3)" }}>{fmtSize(v.size_bytes)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                    {v.uploaded_by_name && <span style={{ fontWeight: 500, color: "var(--text-2)" }}>{v.uploaded_by_name} · </span>}
                    {fmtDateTime(v.uploaded_at)}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="t-btn" title="미리보기"
                      style={{ fontSize: 11, padding: "2px 8px", background: previewVersion === v.version_no ? "var(--p-500,#6366f1)" : undefined, color: previewVersion === v.version_no ? "#fff" : undefined }}
                      onClick={() => setPreviewVersion(prev => prev === v.version_no ? null : v.version_no)}>
                      👁
                    </button>
                    <button className="t-btn" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => downloadVersion(v.version_no)}>⬇️</button>
                    {!isCurrent(v) && (
                      <button className="t-btn" style={{ fontSize: 11, padding: "2px 8px", color: "var(--p-500,#6366f1)" }} onClick={() => restoreVersion(v.version_no)}>🔄</button>
                    )}
                    {!isCurrent(v) && (
                      <button className="t-btn" style={{ fontSize: 11, padding: "2px 8px", color: "#ef4444", marginLeft: "auto" }} onClick={() => deleteVersion(v.version_no)}>🗑️</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Display name (alias) */}
            <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 5 }}>📛 별명 (표시 이름)</div>
              <input className="t-input"
                value={draftDisplayName}
                onChange={(e) => setDraftDisplayName(e.target.value)}
                onBlur={(e) => saveDisplayName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { saveDisplayName(draftDisplayName); (e.target as HTMLElement).blur(); } }}
                placeholder={`비어있으면 파일명 사용`}
                style={{ fontSize: 12, width: "100%", boxSizing: "border-box", padding: "5px 8px" }} />
            </div>
            {/* Description */}
            <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", marginBottom: 5 }}>📝 메모</div>
              <textarea
                value={draftDesc}
                onChange={(e) => setDraftDesc(e.target.value)}
                onBlur={(e) => saveDesc(e.target.value)}
                placeholder="파일에 대한 설명을 입력하세요…"
                style={{
                  width: "100%", boxSizing: "border-box",
                  resize: "vertical", minHeight: 72, maxHeight: 180,
                  fontSize: 12, lineHeight: 1.5,
                  padding: "6px 8px", borderRadius: 6,
                  border: "1px solid var(--border)",
                  background: "var(--panel)", color: "var(--text-1)",
                  fontFamily: "inherit", outline: "none",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "var(--p-400,#818cf8)")}
                onBlurCapture={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
              />
            </div>
            {/* Upload new version */}
            <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)" }}>
              {versionPct !== null && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-3)", marginBottom: 4 }}>
                    <span>업로드 중…</span><span style={{ fontWeight: 700, color: "var(--p-600,#4f46e5)" }}>{versionPct}%</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 4, background: "var(--border)", overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${versionPct}%`, background: "var(--p-500,#6366f1)", borderRadius: 4, transition: "width .2s" }} />
                  </div>
                </div>
              )}
              <input ref={versionInputRef} type="file" hidden onChange={(e) => { const p = e.target.files?.[0]; if (p) uploadVersion(p); }} />
              <button className="t-btn t-btn-primary" style={{ width: "100%", fontSize: 12 }} disabled={versionPct !== null} onClick={() => versionInputRef.current?.click()}>
                {versionPct !== null ? `업로드 중… ${versionPct}%` : "＋ 새 버전 업로드"}
              </button>
            </div>
          </div>
          )} {/* end showGemini ? ... : ... */}
        </div>

        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="t-btn t-btn-primary" onClick={() => downloadFile(f)}>⬇️ 다운로드</button>
          <button className="t-btn" onClick={() => streamDownloadFile(f)} title="대용량 파일용 — 메모리 버퍼링 없이 디스크로 직접 저장" style={{ marginRight: "auto" }}>🌊 스트리밍</button>
          <button className="t-btn" style={{ color: "#ef4444" }} onClick={deleteOpen} title="Del 키로도 삭제">🗑️ 삭제</button>
          <button className="t-btn" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard (overview when no folder selected) ──────────────────────────────

function DriveDashboard({ files, folders }: { files: DriveFile[] | null; folders: DriveFolder[] }) {
  if (!files) return <Loading />;
  const totalSize = files.reduce((s, f) => s + f.size_bytes, 0);
  const rootFolders = folders.filter(f => f.parent_id === null);
  const folderStats = rootFolders.map(folder => ({
    name: folder.name,
    count: files.filter(f => f.folder_id === folder.id).length,
    size: files.filter(f => f.folder_id === folder.id).reduce((s, f) => s + f.size_bytes, 0),
  })).filter(s => s.count > 0).sort((a, b) => b.count - a.count);
  const recent = [...files].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 10);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        {([
          { label: "전체 파일", value: `${files.length}개`, icon: "📁" },
          { label: "폴더", value: `${rootFolders.length}개`, icon: "🗂️" },
          { label: "전체 용량", value: fmtSize(totalSize), icon: "💾" },
        ] as const).map(({ label, value, icon }) => (
          <div key={label} className="t-card" style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 28 }}>{icon}</span>
            <div>
              <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="t-card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>폴더별 현황</div>
          {folderStats.length === 0 && <div style={{ color: "var(--text-3)", fontSize: 13 }}>파일 없음</div>}
          {folderStats.map(s => (
            <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 13, flex: 1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📁 {s.name}</span>
              <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0 }}>{s.count}개</span>
              <span style={{ fontSize: 12, color: "var(--text-3)", flexShrink: 0, minWidth: 56, textAlign: "right" }}>{fmtSize(s.size)}</span>
            </div>
          ))}
        </div>
        <div className="t-card" style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>최근 업로드</div>
          {recent.length === 0 && <div style={{ color: "var(--text-3)", fontSize: 13 }}>파일 없음</div>}
          {recent.map(f => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>{fileEmoji(f)}</span>
              <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{f.name}</span>
              <span style={{ fontSize: 11, color: "var(--text-3)", flexShrink: 0 }}>{fmtDate(f.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Sub-folder tab bar ────────────────────────────────────────────────────────

function SubFolderTabBar({ parentId, scope, activeSubId, canManage, onSelect, onChanged, onFileDrop }: {
  parentId: number; scope: string; activeSubId: string;
  canManage: boolean; onSelect: (id: string) => void; onChanged: () => void;
  onFileDrop: (fileIds: string[], folderId: number) => void;
}) {
  const [subs, setSubs] = useState<DriveFolder[]>([]);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [tabCtx, setTabCtx] = useState<{ x: number; y: number; folder: DriveFolder } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const dragIdx = useRef<number | null>(null);

  const load = useCallback(() => {
    api.get<DriveFolder[]>(`/drive/folders?parent_id=${parentId}&scope=${scope}`)
      .then((r) => setSubs(r.data.sort((a, b) => a.sort_order - b.sort_order)))
      .catch(() => setSubs([]));
  }, [parentId, scope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => setTabCtx(null);
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, []);

  const addSub = async () => {
    const label = newLabel.trim();
    if (!label) { setAdding(false); return; }
    try {
      await api.post("/drive/folders", { name: label, scope, parent_id: parentId });
      setNewLabel(""); setAdding(false); load(); onChanged();
    } catch (e: any) { toast.error("추가 실패", e?.response?.data?.detail ?? ""); }
  };
  const delSub = async (s: DriveFolder) => {
    if (!confirm(`"${s.name}" 폴더와 그 안의 파일을 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/drive/folders/${s.id}`); load(); onChanged();
      if (activeSubId === String(s.id)) onSelect(ALL);
    } catch { toast.error("삭제 실패"); }
  };
  const renameSub = async (id: number) => {
    const label = editLabel.trim();
    if (label) { try { await api.patch(`/drive/folders/${id}`, { name: label }); onChanged(); load(); } catch { toast.error("수정 실패"); } }
    setEditId(null);
  };
  const dropReorder = async (toIdx: number) => {
    const from = dragIdx.current; dragIdx.current = null;
    if (from == null || from === toIdx) return;
    const arr = [...subs]; arr.splice(toIdx, 0, arr.splice(from, 1)[0]);
    setSubs(arr);
    try { await api.put("/drive/folders/reorder", { ids: arr.map(s => s.id) }); onChanged(); }
    catch { toast.error("순서 변경 실패"); load(); }
  };

  const handleFileDrop = (e: React.DragEvent, folderId: number) => {
    e.preventDefault(); setDropTarget(null);
    const raw = e.dataTransfer.getData("application/x-drive-files");
    if (raw) { try { onFileDrop(JSON.parse(raw), folderId); } catch {} }
  };

  const tab = (active: boolean, isDropTarget = false): React.CSSProperties => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "8px 14px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap",
    border: "none", borderBottom: active ? "2px solid var(--p-500,#6366f1)" : "2px solid transparent",
    background: isDropTarget ? "var(--p-50,#eef2ff)" : "transparent",
    color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)",
    fontWeight: active ? 700 : 400,
    marginBottom: -1, borderRadius: isDropTarget ? "6px 6px 0 0" : undefined,
  });

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", padding: "0 16px", borderBottom: "1px solid var(--border)", flexWrap: "wrap", flexShrink: 0, overflowX: "auto" }}>
        <button style={tab(activeSubId === ALL, dropTarget === "all")}
          onClick={() => onSelect(ALL)}
          onDragOver={(e) => { e.preventDefault(); setDropTarget("all"); }}
          onDragLeave={() => setDropTarget(null)}
          onDrop={(e) => handleFileDrop(e, parentId)}>
          전체보기
        </button>
        {subs.map((s, i) => (
          editId === s.id ? (
            <input key={s.id} autoFocus className="t-input" style={{ width: 120, fontSize: 13, padding: "3px 8px", margin: "4px 0 5px 6px" }}
              value={editLabel} onChange={(e) => setEditLabel(e.target.value)}
              onBlur={() => renameSub(s.id)}
              onKeyDown={(e) => { if (e.key === "Enter") renameSub(s.id); if (e.key === "Escape") setEditId(null); }} />
          ) : (
            <button key={s.id} style={tab(activeSubId === String(s.id), dropTarget === String(s.id))}
              onClick={() => onSelect(String(s.id))}
              onContextMenu={(e) => { e.preventDefault(); if (canManage) setTabCtx({ x: e.clientX, y: e.clientY, folder: s }); }}
              draggable={canManage}
              onDragStart={() => { dragIdx.current = i; }}
              onDragOver={(e) => { e.preventDefault(); setDropTarget(String(s.id)); }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) => { const raw = e.dataTransfer.getData("application/x-drive-files"); if (raw) { handleFileDrop(e, s.id); } else { e.preventDefault(); setDropTarget(null); dropReorder(i); } }}>
              📁 {s.name}
            </button>
          )
        ))}
        {canManage && (adding ? (
          <input autoFocus className="t-input" style={{ width: 120, fontSize: 13, padding: "3px 8px", margin: "4px 0 5px 6px" }}
            value={newLabel} placeholder="새 서브폴더…" onChange={(e) => setNewLabel(e.target.value)}
            onBlur={addSub} onKeyDown={(e) => { if (e.key === "Enter") addSub(); if (e.key === "Escape") { setAdding(false); setNewLabel(""); } }} />
        ) : (
          <button style={{ ...tab(false), color: "var(--p-500,#6366f1)", fontWeight: 500 }} onClick={() => setAdding(true)}>＋ 서브폴더</button>
        ))}
      </div>
      {tabCtx && (
        <div style={{ position: "fixed", top: tabCtx.y, left: tabCtx.x, background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 8, padding: "4px 0", zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,.15)", minWidth: 140 }}
          onClick={(e) => e.stopPropagation()}>
          <button style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 14px", fontSize: 13, border: "none", cursor: "pointer", background: "transparent", color: "var(--text-1)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            onClick={() => { setEditId(tabCtx.folder.id); setEditLabel(tabCtx.folder.name); setTabCtx(null); }}>
            ✏️ 이름 변경
          </button>
          <button style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 14px", fontSize: 13, border: "none", cursor: "pointer", background: "transparent", color: "#ef4444" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            onClick={() => { delSub(tabCtx.folder); setTabCtx(null); }}>
            🗑️ 삭제
          </button>
        </div>
      )}
    </>
  );
}

// ── Rename modal ──────────────────────────────────────────────────────────────

function RenameModal({ file, onClose, onDone }: { file: DriveFile; onClose: () => void; onDone: () => void }) {
  const dot = file.name.lastIndexOf(".");
  const stem0 = dot > 0 ? file.name.slice(0, dot) : file.name;
  const ext = dot > 0 ? file.name.slice(dot) : "";
  const [name, setName] = useState(stem0);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const submit = async () => {
    const fullName = name.trim() + ext;
    if (!name.trim() || fullName === file.name) { onClose(); return; }
    setSaving(true);
    try { await api.patch(`/drive/files/${file.id}`, { name: fullName }); toast.success("이름 변경됨"); onDone(); onClose(); }
    catch (e: any) { toast.error("이름 변경 실패", e?.response?.data?.detail ?? ""); }
    finally { setSaving(false); }
  };
  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: "95vw" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)" }}><h3 style={{ margin: 0, fontSize: 14 }}>파일 이름 변경</h3></div>
        <div style={{ padding: 16 }}>
          <label className="t-label">새 이름</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input ref={ref} className="t-input" style={{ flex: 1 }} value={name} onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onClose(); }} />
            {ext && <span style={{ color: "var(--text-3)", fontSize: 13, flexShrink: 0, userSelect: "none" }}>{ext}</span>}
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={saving}>{saving ? "변경 중…" : "변경"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Context button helper ─────────────────────────────────────────────────────

function CtxBtn({ label, icon, onClick, danger }: { label: string; icon: string; onClick: () => void; danger?: boolean }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 14px", fontSize: 13, border: "none", cursor: "pointer", background: "transparent", textAlign: "left", color: danger ? "#ef4444" : "var(--text-1)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
      <span>{icon}</span><span>{label}</span>
    </button>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

// fixedScope: /exec/docs(내부문서)가 SECRET으로 고정해 재사용. 미지정 시 ?filter= 기반.
export default function DrivePage({ fixedScope }: { fixedScope?: string } = {}) {
  const [params, setParams] = useSearchParams();
  const scopeParam = params.get("filter") || "company";
  // 내부문서(SECRET)는 드라이브 메뉴에서 완전 차단 — /exec/docs 전용. 진입 시 전사로 교정.
  const scope = fixedScope ?? (scopeParam.toUpperCase() === "SECRET" ? "COMPANY" : scopeParam.toUpperCase());
  useEffect(() => {
    if (!fixedScope && scopeParam.toLowerCase() === "secret") {
      setParams(p => { const n = new URLSearchParams(p); n.set("filter", "company"); n.delete("folder"); n.delete("sub"); return n; }, { replace: true });
    }
  }, [scopeParam]);
  const activeFolderId = params.get("folder") || ALL;
  const activeSubId = params.get("sub") || ALL;

  const [folders, setFolders] = useState<DriveFolder[]>([]);
  const [files, setFiles] = useState<DriveFile[] | null>(null);
  const [viewMode, setViewMode] = useState<"gallery" | "list">(() => (localStorage.getItem("drive-view") as any) || "list");
  const [detail, setDetail] = useState<DriveFile | null>(null);
  const [renaming, setRenaming] = useState<DriveFile | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; file: DriveFile } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cols, setCols] = useState<number>(() => { const v = Number(localStorage.getItem("drive-cols")); return v >= 1 && v <= 9 ? v : 5; });
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"name" | "modified" | "size" | "version">(() => (localStorage.getItem("drive-sort") as any) || "modified");
  const [dragOver, setDragOver] = useState(false);
  const [uploadJobs, setUploadJobs] = useState<{ id: string; name: string; pct: number }[]>([]);
  const dragCounter = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastIdx = useRef<number | null>(null);
  const [rubberBand, setRubberBand] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const rubberActive = useRef(false);
  const rubberStart = useRef({ x: 0, y: 0 });
  const gridRef = useRef<HTMLDivElement>(null);

  const canManage = useAuth((s) => s.hasRole("SUPER_ADMIN") || s.hasRole("MANAGER") || s.hasRole("TEAM_LEAD") || s.hasRole("DIVISION_HEAD") || s.hasRole("C_LEVEL") || s.hasRole("CEO") || s.hasRole("HR_ADMIN"));
  const isSuperAdmin = useAuth((s) => s.hasRole("SUPER_ADMIN"));
  const [syncing, setSyncing] = useState(false);

  // 서버 폴더에 직접 넣은 파일(예: 대용량 ISO)을 DB에 등록 → 드라이브에 표시.
  const syncServer = async () => {
    if (activeFolderId === ALL) return;
    setSyncing(true);
    try {
      const r = await api.post<{ imported: number }>(`/drive/folders/${activeFolderId}/sync`);
      if (r.data.imported > 0) { toast.success(`서버 파일 ${r.data.imported}개 동기화됨`); refresh(); }
      else toast.info("새로 추가된 서버 파일이 없습니다");
    } catch (e: any) { toast.error("서버 동기화 실패", e?.response?.data?.detail ?? ""); }
    finally { setSyncing(false); }
  };

  const setView = (v: "gallery" | "list") => { setViewMode(v); localStorage.setItem("drive-view", v); };
  const changeCols = (n: number) => { const v = Math.max(1, Math.min(9, n)); setCols(v); localStorage.setItem("drive-cols", String(v)); };
  const changeSort = (s: "name" | "modified" | "size" | "version") => { setSort(s); localStorage.setItem("drive-sort", s); };

  const setSub = (id: string) => setParams(p => { const n = new URLSearchParams(p); if (id === ALL) n.delete("sub"); else n.set("sub", id); return n; }, { replace: false });

  // Load root folders
  const loadFolders = useCallback(() => {
    api.get<DriveFolder[]>(`/drive/folders?scope=${scope}&root_only=true`)
      .then((r) => setFolders(r.data.sort((a, b) => a.sort_order - b.sort_order)))
      .catch(() => setFolders([]));
  }, [scope]);
  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { setSelected(new Set()); lastIdx.current = null; }, [scope, activeFolderId, activeSubId]);

  // Load files
  const loadFiles = useCallback(async () => {
    setFiles(null);
    try {
      const qs = new URLSearchParams({ scope });
      if (activeSubId !== ALL) qs.set("folder_id", activeSubId);
      else if (activeFolderId !== ALL) qs.set("parent_folder_id", activeFolderId);
      const r = await api.get<DriveFile[]>(`/drive/files?${qs}`);
      setFiles(r.data);
    } catch { setFiles([]); }
  }, [scope, activeFolderId, activeSubId]);
  useEffect(() => { loadFiles(); }, [loadFiles]);

  const refresh = () => loadFiles();

  // 로그인 역할별 업로드 한도를 받아온다 (마운트 1회). 최종 검증은 백엔드.
  useEffect(() => {
    api.get<{ limit_bytes: number | null; label: string }>("/drive/upload-limit")
      .then(r => { uploadLimitBytes = r.data.limit_bytes; uploadLimitLabel = r.data.label; })
      .catch(() => {});
  }, []);

  // Upload
  const doUpload = async (picked: File[]) => {
    const oversized = uploadLimitBytes !== null ? picked.filter(f => f.size > uploadLimitBytes!) : [];
    if (oversized.length) { toast.error("파일 크기 초과", `${oversized.map(f => f.name).join(", ")} — 최대 ${uploadLimitLabel}`); return; }
    let targetFolderId: number | null = null;
    if (activeSubId !== ALL) {
      targetFolderId = Number(activeSubId);
    } else if (activeFolderId !== ALL) {
      targetFolderId = Number(activeFolderId);   // 현재 보고 있는 폴더에 그대로 업로드 (서버 경로도 그 폴더)
    } else {
      toast.error("폴더를 선택하세요", "위에서 폴더를 먼저 선택하세요"); return;
    }
    let ok = 0;
    for (const file of picked) {
      const jobId = `${Date.now()}-${file.name}`;
      setUploadJobs(j => [...j, { id: jobId, name: file.name, pct: 0 }]);
      const fd = new FormData();
      fd.append("file", file); fd.append("scope", scope); fd.append("folder_id", String(targetFolderId));
      try {
        await api.post("/drive/files", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setUploadJobs(j => j.map(x => x.id === jobId ? { ...x, pct: Math.round(e.loaded / e.total! * 100) } : x));
          },
        });
        ok++;
      } catch (e: any) { toast.error("업로드 실패", `${file.name}: ${e?.response?.data?.detail ?? ""}`); }
      setUploadJobs(j => j.filter(x => x.id !== jobId));
    }
    if (ok) { toast.success(`${ok}개 업로드 완료`); refresh(); }
  };

  // Drag-drop
  const onDragEnter = (e: React.DragEvent) => { e.preventDefault(); dragCounter.current++; if (e.dataTransfer.types.includes("Files")) setDragOver(true); };
  const onDragLeave = () => { dragCounter.current--; if (dragCounter.current === 0) setDragOver(false); };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); dragCounter.current = 0; setDragOver(false);
    const d = Array.from(e.dataTransfer.files); if (d.length) doUpload(d);
  };

  useEffect(() => { const c = () => setCtxMenu(null); document.addEventListener("click", c); return () => document.removeEventListener("click", c); }, []);

  // Rubber-band drag selection
  const onGridMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const t = e.target as HTMLElement;
    if (t.closest('[data-refkey]') || t.closest('button') || t.closest('input') || t.closest('select')) return;
    rubberActive.current = true;
    rubberStart.current = { x: e.clientX, y: e.clientY };
    setRubberBand({ x1: e.clientX, y1: e.clientY, x2: e.clientX, y2: e.clientY });
    setSelected(new Set());
  };
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!rubberActive.current) return;
      const { clientX: x2, clientY: y2 } = e;
      setRubberBand(prev => prev ? { ...prev, x2, y2 } : null);
      const bL = Math.min(rubberStart.current.x, x2), bR = Math.max(rubberStart.current.x, x2);
      const bT = Math.min(rubberStart.current.y, y2), bB = Math.max(rubberStart.current.y, y2);
      const hit = new Set<string>();
      gridRef.current?.querySelectorAll<HTMLElement>('[data-refkey]').forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.left < bR && r.right > bL && r.top < bB && r.bottom > bT) hit.add(el.dataset.refkey!);
      });
      setSelected(hit);
    };
    const onUp = () => { if (!rubberActive.current) return; rubberActive.current = false; setRubberBand(null); };
    const onDragStartGlobal = () => { rubberActive.current = false; setRubberBand(null); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('dragstart', onDragStartGlobal);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); document.removeEventListener('dragstart', onDragStartGlobal); };
  }, []);

  // Search + sort
  const displayFiles = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = (files ?? []).filter(f => !q || f.name.toLowerCase().includes(q));
    if (sort === "name") arr.sort((a, b) => a.name.localeCompare(b.name, "ko"));
    else if (sort === "modified") arr.sort((a, b) => b.created_at.localeCompare(a.created_at));
    else if (sort === "size") arr.sort((a, b) => b.size_bytes - a.size_bytes);
    else if (sort === "version") arr.sort((a, b) => b.version_count - a.version_count);
    return arr;
  }, [files, search, sort]);

  const isSel = (f: DriveFile) => selected.has(fileKey(f));
  const toggleSel = (f: DriveFile, idx: number) => {
    setSelected(prev => { const n = new Set(prev); const k = fileKey(f); n.has(k) ? n.delete(k) : n.add(k); return n; });
    lastIdx.current = idx;
  };
  const onCardClick = (f: DriveFile, idx: number, e: React.MouseEvent) => {
    if (e.shiftKey && lastIdx.current != null) {
      const a = Math.min(lastIdx.current, idx), b = Math.max(lastIdx.current, idx);
      setSelected(prev => { const n = new Set(prev); for (let i = a; i <= b; i++) if (displayFiles[i]) n.add(fileKey(displayFiles[i])); return n; });
    } else if (e.ctrlKey || e.metaKey) {
      toggleSel(f, idx);
    } else {
      setDetail(f);
    }
  };

  const deleteFile = async (f: DriveFile) => {
    if (!confirm(`"${f.name}"을 삭제하시겠습니까?`)) return;
    try { await api.delete(`/drive/files/${f.id}`); toast.success("삭제됨"); refresh(); } catch { toast.error("삭제 실패"); }
  };
  const bulkDelete = async () => {
    const fs = displayFiles.filter(isSel); if (!fs.length) return;
    if (!confirm(`선택한 ${fs.length}개를 삭제하시겠습니까?`)) return;
    for (const f of fs) { try { await api.delete(`/drive/files/${f.id}`); } catch { /* keep going */ } }
    toast.success(`${fs.length}개 삭제됨`); setSelected(new Set()); refresh();
  };

  const moveFiles = async (fileIds: string[], folderId: number) => {
    const targets = displayFiles.filter(f => fileIds.includes(fileKey(f)));
    if (!targets.length) return;
    let ok = 0;
    for (const f of targets) {
      try { await api.patch(`/drive/files/${f.id}`, { folder_id: folderId }); ok++; }
      catch { toast.error("이동 실패", f.name); }
    }
    if (ok) { toast.success(`${ok}개 이동됨`); setSelected(new Set()); refresh(); }
  };

  const onFileDragStart = (e: React.DragEvent, f: DriveFile) => {
    const ids = selected.has(fileKey(f)) ? [...selected] : [fileKey(f)];
    e.dataTransfer.setData("application/x-drive-files", JSON.stringify(ids));
    e.dataTransfer.effectAllowed = "move";
  };

  // Refresh when DriveSidebar moves files via drag-drop
  useEffect(() => {
    const h = () => refresh();
    window.addEventListener("drive:refresh", h);
    return () => window.removeEventListener("drive:refresh", h);
  }, [activeFolderId, scope]);

  // Del key → delete the selected files (grid). The detail modal handles its own Del.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== "Delete") return;
      if (detail || renaming) return;   // a modal is open → it handles Del itself
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (selected.size === 0) return;
      e.preventDefault();
      bulkDelete();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [detail, renaming, selected, displayFiles]);

  const activeFolder = folders.find(f => String(f.id) === activeFolderId) ?? null;
  const isDashboard = activeFolderId === ALL;
  const headerLabel = isDashboard ? "드라이브" : (activeFolder?.name ?? "드라이브");
  const scopeLabel = SCOPE_LABELS[scope] ?? scope;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}
      onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      {dragOver && (
        <div style={{ position: "absolute", inset: 0, zIndex: 500, background: "rgba(99,102,241,.12)", border: "3px dashed var(--p-400,#818cf8)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{ textAlign: "center", color: "var(--p-500,#6366f1)" }}>
            <div style={{ fontSize: 40 }}>📂</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{isDashboard ? "폴더를 먼저 선택하세요" : `'${activeFolder?.name ?? ""}' 폴더에 업로드`}</div>
          </div>
        </div>
      )}

      <PageHeader
        title={headerLabel}
        subtitle={isDashboard ? `${scopeLabel} · 대시보드` : `${scopeLabel}${activeFolder ? ` · ${activeFolder.name}` : ""}`}
        right={<div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {viewMode === "gallery" && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, height: 32, padding: "0 10px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--panel)" }}>
              <span style={{ fontSize: 11, color: "var(--text-3)" }}>열</span>
              <input type="range" min={1} max={9} step={1} value={cols} onChange={(e) => changeCols(Number(e.target.value))} style={{ width: 80, accentColor: "var(--p-500,#6366f1)", cursor: "pointer" }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-2)", width: 10, textAlign: "center" }}>{cols}</span>
            </div>
          )}
          <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
            <button className="t-btn" onClick={() => setView("gallery")} style={{ border: "none", borderRadius: 0, background: viewMode === "gallery" ? "var(--p-500,#6366f1)" : "var(--panel)", color: viewMode === "gallery" ? "#fff" : undefined, padding: "5px 10px" }}>▦</button>
            <button className="t-btn" onClick={() => setView("list")} style={{ border: "none", borderRadius: 0, background: viewMode === "list" ? "var(--p-500,#6366f1)" : "var(--panel)", color: viewMode === "list" ? "#fff" : undefined, padding: "5px 10px" }}>☰</button>
          </div>
          <input ref={inputRef} type="file" multiple hidden onChange={(e) => { const p = Array.from(e.target.files ?? []); if (p.length) doUpload(p); if (inputRef.current) inputRef.current.value = ""; }} />
          {!isDashboard && isSuperAdmin && (
            <button className="t-btn" onClick={syncServer} disabled={syncing}
              title="서버 폴더에 직접 넣은 파일(대용량 ISO 등)을 드라이브에 등록">
              {syncing ? "동기화 중…" : "🔄 서버 동기화"}
            </button>
          )}
          {!isDashboard && (
            <button className="t-btn t-btn-primary" onClick={() => inputRef.current?.click()}><I.Plus size={14} /> 파일 추가</button>
          )}
        </div>}
      />

      {/* Sub-folder tab bar — only when a root folder is selected */}
      {!isDashboard && activeFolder && (
        <SubFolderTabBar parentId={activeFolder.id} scope={scope} activeSubId={activeSubId}
          canManage={canManage} onSelect={setSub} onChanged={refresh} onFileDrop={moveFiles} />
      )}

      {/* Dashboard */}
      {isDashboard && <DriveDashboard files={files} folders={folders} />}

      {/* File grid / list */}
      {!isDashboard && (
        <div ref={gridRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px", userSelect: rubberBand ? "none" : undefined }}
          onMouseDown={onGridMouseDown}>
          {/* Search + sort */}
          {files && files.length > 0 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: "0 1 280px", minWidth: 180 }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--text-3)", pointerEvents: "none" }}>🔍</span>
                <input className="t-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="파일명 검색" style={{ height: 34, fontSize: 13, paddingLeft: 30, width: "100%", boxSizing: "border-box" }} />
              </div>
              <select value={sort} onChange={(e) => changeSort(e.target.value as any)}
                style={{ height: 34, borderRadius: 8, border: "1px solid var(--border)", background: "var(--panel)", color: "var(--text-2)", fontSize: 13, padding: "0 8px", cursor: "pointer" }}>
                <option value="name">이름순</option>
                <option value="modified">최신순</option>
                <option value="size">크기순</option>
                <option value="version">버전순</option>
              </select>
              <span style={{ fontSize: 12, color: "var(--text-3)", marginLeft: "auto" }}>{search ? `${displayFiles.length} / ${files.length}개` : `${files.length}개`}</span>
            </div>
          )}

          {/* Selection toolbar */}
          {selected.size > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", marginBottom: 12, background: "var(--p-50,#eef2ff)", border: "1px solid var(--p-200,#c7d2fe)", borderRadius: 8, position: "sticky", top: -16, zIndex: 5 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--p-700,#4338ca)" }}>{selected.size}개 선택</span>
              <button className="t-btn" onClick={() => setSelected(new Set(displayFiles.map(fileKey)))}>전체 선택</button>
              <button className="t-btn" onClick={() => displayFiles.filter(isSel).forEach(downloadFile)}>⬇️ 다운로드</button>
              <button className="t-btn" style={{ color: "#ef4444" }} onClick={bulkDelete}>🗑️ 삭제</button>
              <button className="t-btn" style={{ marginLeft: "auto" }} onClick={() => setSelected(new Set())}>선택 해제</button>
            </div>
          )}

          {files === null && <Loading />}
          {files?.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text-3)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-2)" }}>파일이 없습니다</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>드래그하거나 '파일 추가' 버튼으로 업로드하세요</div>
            </div>
          )}

          {/* Gallery */}
          {files && displayFiles.length > 0 && viewMode === "gallery" && (
            <div style={{ columnCount: cols, columnGap: 14 }}>
              {displayFiles.map((f, idx) => (
                <div key={f.id} data-refkey={fileKey(f)}
                  draggable
                  onDragStart={(e) => onFileDragStart(e, f)}
                  style={{ width: "100%", breakInside: "avoid", marginBottom: 14, position: "relative", background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", cursor: "grab", transition: "box-shadow .15s, transform .15s", outline: isSel(f) ? "2px solid var(--p-500,#6366f1)" : undefined, outlineOffset: -2, userSelect: "none" }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                  onClick={(e) => onCardClick(f, idx, e)}
                  onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, file: f }); }}>
                  <div onClick={(e) => { e.stopPropagation(); toggleSel(f, idx); }}
                    style={{ position: "absolute", top: 6, left: 6, zIndex: 2, width: 20, height: 20, borderRadius: 5, border: "2px solid #fff", background: isSel(f) ? "var(--p-500,#6366f1)" : "rgba(0,0,0,.28)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.35)" }}>
                    {isSel(f) && <span style={{ color: "#fff", fontSize: 12, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                  </div>
                  {f.version_count > 1 && (
                    <span style={{ position: "absolute", top: 6, right: 6, zIndex: 2, fontSize: 10, background: "var(--p-500,#6366f1)", color: "#fff", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>v{f.version_count}</span>
                  )}
                  <div style={{ width: "100%", paddingBottom: "70%", position: "relative", background: "var(--bg-2,#f3f4f6)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FileThumbnail f={f} />
                    </div>
                  </div>
                  <div style={{ padding: "8px 10px" }}>
                    <div style={{ fontSize: 12, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{dispName(f)}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2, display: "flex", justifyContent: "space-between" }}>
                      <span>{fmtDate(f.created_at)}</span>
                      <span>{fmtSize(f.size_bytes)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* List */}
          {files && displayFiles.length > 0 && viewMode === "list" && (
            <div className="t-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "28px 38px 1fr 80px 90px 80px 50px", padding: "8px 14px", borderBottom: "2px solid var(--border)", fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: ".04em", gap: 8, alignItems: "center" }}>
                {/* Select-all checkbox */}
                <div onClick={() => { const allSelected = selected.size === displayFiles.length && displayFiles.length > 0; setSelected(allSelected ? new Set() : new Set(displayFiles.map(fileKey))); }}
                  style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected.size > 0 && selected.size === displayFiles.length ? "var(--p-500,#6366f1)" : "var(--border)"}`, background: selected.size > 0 && selected.size === displayFiles.length ? "var(--p-500,#6366f1)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                  {selected.size > 0 && selected.size === displayFiles.length && <span style={{ color: "#fff", fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                  {selected.size > 0 && selected.size < displayFiles.length && <span style={{ color: "var(--p-500,#6366f1)", fontSize: 12, fontWeight: 900, lineHeight: 1 }}>–</span>}
                </div>
                <span></span>
                <span>이름</span>
                <span style={{ textAlign: "center" }}>버전</span>
                <span style={{ textAlign: "right" }}>크기</span>
                <span style={{ textAlign: "center" }}>수정일</span>
                <span></span>
              </div>
              {displayFiles.map((f, idx) => (
                <div key={f.id} data-refkey={fileKey(f)}
                  draggable
                  onDragStart={(e) => onFileDragStart(e, f)}
                  style={{ display: "grid", gridTemplateColumns: "28px 38px 1fr 80px 90px 80px 50px", alignItems: "center", padding: "7px 14px", borderBottom: "1px solid var(--border)", gap: 8, cursor: "grab", background: isSel(f) ? "rgba(99,102,241,.07)" : undefined, userSelect: "none" }}
                  onClick={(e) => onCardClick(f, idx, e)}
                  onContextMenu={(e) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, file: f }); }}>
                  {/* Checkbox */}
                  <div onClick={(e) => { e.stopPropagation(); toggleSel(f, idx); }}
                    style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isSel(f) ? "var(--p-500,#6366f1)" : "var(--border)"}`, background: isSel(f) ? "var(--p-500,#6366f1)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                    {isSel(f) && <span style={{ color: "#fff", fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>}
                  </div>
                  {/* File icon / thumbnail */}
                  <div style={{ width: 32, height: 28, borderRadius: 4, background: "var(--bg-2,#f3f4f6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, overflow: "hidden", flexShrink: 0 }}>
                    <FileThumbnail f={f} style={{ borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={f.name}>{dispName(f)}</span>
                  <span style={{ fontSize: 12, textAlign: "center" }}>
                    {f.version_count > 0 && <span style={{ fontSize: 11, padding: "1px 6px", borderRadius: 4, background: f.version_count > 1 ? "var(--p-50,#eef2ff)" : "var(--panel)", color: f.version_count > 1 ? "var(--p-600,#4f46e5)" : "var(--text-3)", fontWeight: 600 }}>v{f.version_count}</span>}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-3)", textAlign: "right" }}>{fmtSize(f.size_bytes)}</span>
                  <span style={{ fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>{fmtDate(f.created_at)}</span>
                  <span style={{ textAlign: "center", color: "var(--text-3)" }} onClick={(e) => { e.stopPropagation(); setCtxMenu({ x: e.clientX, y: e.clientY, file: f }); }}>⋯</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rubber-band selection rect */}
      {rubberBand && (
        <div style={{ position: "fixed", pointerEvents: "none", zIndex: 998,
          left: Math.min(rubberBand.x1, rubberBand.x2), top: Math.min(rubberBand.y1, rubberBand.y2),
          width: Math.abs(rubberBand.x2 - rubberBand.x1), height: Math.abs(rubberBand.y2 - rubberBand.y1),
          background: "rgba(99,102,241,.1)", border: "1px solid rgba(99,102,241,.55)", borderRadius: 3 }} />
      )}

      {/* Context menu */}
      {ctxMenu && (
        <div style={{ position: "fixed", top: ctxMenu.y, left: ctxMenu.x, background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 8, padding: "4px 0", zIndex: 9999, boxShadow: "0 4px 20px rgba(0,0,0,.15)", minWidth: 160, fontSize: 13 }}
          onClick={(e) => e.stopPropagation()}>
          <CtxBtn label="열기 / 버전 이력" icon="🕓" onClick={() => { setDetail(ctxMenu.file); setCtxMenu(null); }} />
          <CtxBtn label="이름 변경" icon="✏️" onClick={() => { setRenaming(ctxMenu.file); setCtxMenu(null); }} />
          <CtxBtn label="다운로드" icon="⬇️" onClick={() => { downloadFile(ctxMenu.file); setCtxMenu(null); }} />
          <CtxBtn label="스트리밍 다운로드 (대용량)" icon="🌊" onClick={() => { streamDownloadFile(ctxMenu.file); setCtxMenu(null); }} />
          <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
          <CtxBtn label="삭제" icon="🗑️" danger onClick={() => { deleteFile(ctxMenu.file); setCtxMenu(null); }} />
        </div>
      )}

      {detail && (
        <FileDetail
          files={displayFiles}
          initialIndex={Math.max(0, displayFiles.findIndex(fi => fi.id === detail!.id))}
          onClose={() => setDetail(null)}
          onDeleted={refresh}
          onVersionUploaded={refresh}
        />
      )}
      {renaming && <RenameModal file={renaming} onClose={() => setRenaming(null)} onDone={refresh} />}

      {/* Upload progress panel */}
      {uploadJobs.length > 0 && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9990, display: "flex", flexDirection: "column", gap: 8, minWidth: 280, maxWidth: 360 }}>
          {uploadJobs.map(job => (
            <div key={job.id} style={{ background: "var(--bg-0,#fff)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px", boxShadow: "0 4px 20px rgba(0,0,0,.15)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>⬆️ {job.name}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--p-600,#4f46e5)", flexShrink: 0 }}>{job.pct}%</span>
              </div>
              <div style={{ height: 6, borderRadius: 4, background: "var(--border)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${job.pct}%`, background: "var(--p-500,#6366f1)", borderRadius: 4, transition: "width .15s" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
